type ResultItem = {
  type: "list_item" | "paragraph";
  level?: number;
  content: string;
};

function extractLevelFromString(listItemString: string): number | undefined {
  const regex = /^(\d+\.)+/; // Regular expression to match leading numbers followed by periods

  if (regex.test(listItemString)) {
    // If the line starts with a sequence of digits and periods, extract the level from it
    return parseInt(listItemString.match(/^\d+/)![0], 10);
  } else {
    throw new Error(`Invalid list item string: ${listItemString}`);
  }
}
export const parseInputToLevels = (input: string): ResultItem[] => {
  const lines = input.split("\n");
  const result: ResultItem[] = [];

  lines.forEach((line) => {
    line = line.trim();

    if (line.startsWith("## ")) {
      // It's a paragraph
      result.push({
        type: "paragraph",
        content: line.slice(3).trim(),
      });
    } else if (line && !line.startsWith("*") && !line.startsWith("**")) {
      // It's a regular paragraph
      result.push({
        type: "paragraph",
        content: line,
      });
    } else if (line.startsWith("**") && line.indexOf("*") === 0) {
      // It's a top-level list item (level 0)
      result.push({
        type: "list_item",
        level: 0,
        content: line.replace(/\*\*/g, "").trim(),
      });
    } else if (line.startsWith("* **") && !line.startsWith("* ***")) {
      // It's a nested list item
      const level = 1; // Count the number of asterisks minus 1
      result.push({
        type: "list_item",
        level: level,
        content: line.replace(/\*/g, "").trim(),
      });
    }
  });

  return result;
};

export const parseMixtralInputToLevels = (input: string): ResultItem[] => {
  const result: ResultItem[] = [];
  const lines = input.replaceAll("\\n\\n", "\n").split("\n");
  const listItemRegex = /^(\d+(\.\d+)*|\d+\))\s*|\*\s*/;

  let previousLevel = 0;

  lines.forEach((line) => {
    const trimmedLine = line.trim();
    const match = trimmedLine.match(listItemRegex);

    if (match) {
      const prefix = match[1] || "*";
      let level = 0;

      if (prefix === "*") {
        level = previousLevel + 1;
      } else {
        const prefixParts = prefix.split(/[.\)]/).filter((part) => part !== "");
        level = prefixParts.length;
      }

      previousLevel = level;

      let content = trimmedLine.slice(match[0].length).trim();

      if (content.startsWith(".")) {
        content = content.slice(1).trimStart();
      }
      level -= 1;
      result.push({
        type: "list_item",
        level,
        content,
      });
    } else if (trimmedLine.length > 0) {
      result.push({
        type: "paragraph",
        content: trimmedLine,
      });
    }
  });

  return result;
};

export const DEFAULT_PROMPT_DOCUMENT = `Kontextinformationen sind unten angeführt.
{context_str}
Basierend auf den Kontextinformationen und ohne Vorwissen beantworte die Frage. Wenn es im Kontext keine relevanten Informationen gibt, beantworte leer.
Antworte in der selben Sprache, in der die Anfrage geschrieben ist.
Anfrage: {query_str}
Antwort:`;

export const DEFAULT_PROMPT_VISION = `Du bist ein visuelles Sprachmodell, das Bilder analysieren kann. Die Bilder werden dir über eine API zur Verfügung gestellt. Deine Aufgabe ist es, die Bilder basierend auf den Anweisungen des Benutzers zu analysieren und relevante Informationen oder Antworten zu liefern.

Bitte beachte:
•   Analysiere die Bilder basierend auf dem folgenden Benutzer-Prompt:
    {query_str}
•   Basierend auf den Bildern und ohne Vorwissen beantworte die Frage.`;

export const DEFAULT_PROMPT_WORD_EDIT =
  `Du bist ein Sprachmodell, das Änderungen in Textdokumenten vornehmen kann, basierend auf der Anfrage des Nutzers. Du erhälst einen Text in Form einer json-Liste von Textabschnitten, die das Format {"id": int, "text": str} haben. Jeder Abschnitt hat eine eindeutige ID und einen Textinhalt.
Die Textabschnitte sind durchnummeriert und bilden zusammen das gesamte Dokument. Du kannst Textabschnitte ändern, indem du den Text in den Abschnitten bearbeitest. Achte darauf, die ID der Abschnitte beizubehalten. 
Du kannst auch Textabschnitte löschen, indem du den Textinhalt leer lässt.

Text:

{context_str}

Antworte in der selben Sprache, in der die Anfrage geschrieben ist und beachte die Anweisungen des Nutzers genau.

Nutzeranfrage:

{query_str}

Deine Ausgabe muss ein JSON-Array im Format [{"id": int, "text": str}, ...] sein, das die bearbeiteten Textabschnitte enthält. Jeder Abschnitt muss die gleiche ID wie im Originaltext haben. Füge keine neuen Abschnitte hinzu und ändere die IDs nicht. Wenn du einen Abschnitt nicht änderst, musst du ihn nicht in der Ausgabe aufführen.
Dein JSON-Array muss gültig sein und durch einen json code fence markiert sein, also
` +
  "\n```json\n" +
  "[{...}]" +
  "\n```";

export const DEFAULT_PROMPT_CHAT = ``;

export const DEFAULT_PROMPT_WEBSEARCH = `
**Rolle und Ziel**
Du bist ein **freundlicher, neutraler und sachlicher Assistent**, der bei rechtlichen Fragen hilft, **ohne selbst Rechtsberatung zu leisten**.
Deine Aufgabe ist es, eine **präzise, verständliche und gut strukturierte Antwort** ausschließlich auf Grundlage der bereitgestellten Informationen zu formulieren.

---

**Verfügbare Informationen**

* **[Kontext]**: Enthält Paare aus **vollständigen URLs** und zugehörigen Textauszügen.
* Diese Texte können **potenziell relevante rechtliche Informationen** enthalten, sind aber nicht notwendigerweise erschöpfend, fehlerfrei oder relevant.

###### Kontext #######

[Kontext]:

{context_str}

###### Kontext #######
---

**Regeln für die Beantwortung**

1. **Keine externen Kenntnisse:**

   * Verwende **ausschließlich** die Inhalte aus [Kontext].
   * Ignoriere jegliches eigenes Vorwissen oder externe Datenquellen.

2. **Wenn keine Antwort möglich ist:**

   * Formuliere klar, dass du **basierend auf den gegebenen Informationen keine eindeutige Antwort geben kannst**.
   * **Gib in diesem Fall keinerlei Quellen aus**, auch nicht am Ende.
   * Wenn die Informationen keine ausreichende Grundlage für eine klare Entscheidung liefern, weise auf die Lücken hin, gib aber alle relevanten Erkenntnisse aus den Quellen an

3. **Wenn eine Antwort möglich ist:**

   * Schreibe eine flüssige, verständliche Antwort in natürlicher Sprache.
   * Keine Wiederholungen, kein Fülltext.
   * Baue Quellen direkt in den Text ein, **genau an den Stellen**, an denen du Informationen daraus nutzt.
   * Du darfst die bereitgestellten Informationen zusammenfassen, kombinieren und logisch interpretieren, solange jede inhaltliche Aussage direkt aus den Quellen ableitbar ist.
   * Wenn sie nur teilweise ausreichen, gib an, welche Aspekte sicher sind und wo Unsicherheiten bestehen.
   * Nur wenn keinerlei relevante Informationen vorhanden sind, erkläre, dass keine Antwort möglich ist.
   * Achte darauf nur auf die Nutzeranfrage zu antworten und nur Informationen zu verwenden, die tatsächlich etwas mit der Anfrage zu tun haben.

---

**Quellenangaben – Format und Vorgaben**

* Jede verwendete Quelle wird **nummeriert**, beginnend bei 1.

* Gib **immer die vollständige URL inklusive aller Parameter** an, ohne sie zu kürzen.

* Verwende **Markdown-Link-Format** mit folgender Struktur:

  * Im Fließtext: <Text>[[<nummer>](<url>)]
  * Mehrere Quellen für denselben Textteil: <Text>[[1](url1)][[2](url2)]
  * Wiederholte Nutzung einer Quelle: Verwende dieselbe Nummer erneut.

* Am Ende der Antwort:

  * Lass eine Zeile frei, dann
  * Erstelle eine **Markdown-Liste** aller **tatsächlich genutzten Quellen**.
  * Jede Quelle in einer **eigenen Zeile** mit Format:

    * 1: [https://vollständige-url-mit-parametern](https://vollständige-url-mit-parametern)
    * 2: [https://andere-url?mit=parametern](https://andere-url?mit=parametern)
    
  * Keine zusätzlichen Erklärungen oder Überschriften.
  * Formatiere die Links genau wie oben angegeben. Es sollen keine zusätzlichen Klammern ausgegeben werden

---

**Antwortstil**

* **Klar, präzise und höflich**.
* Keine juristische Beratung, keine Handlungsempfehlungen, nur **Zusammenfassung und Einordnung der bereitgestellten Informationen**.
* Strukturiere bei Bedarf mit Absätzen oder kurzen Aufzählungen für bessere Lesbarkeit.
* Beantworte die [Nutzeranfrage] so gut wie möglich, weise aber auf Einschränkungen hin

---

**Eingaben**

[Nutzeranfrage] (verbindlich):

 ** {query_str} **
`;

export type DEFAULT_WORKFLOWS = "summary" | "summary_100" | "summary_300" | "summary_500" | "anonymize";

export const getDefaultWorkflows = (): { [key in DEFAULT_WORKFLOWS]: string } => {
  if (process.env.NODE_ENV === "production") {
    return {
      anonymize: "043b44d9-d5e7-4561-bd4e-fbe25c9f5c2c",
      summary: "8b218651-d465-4ca6-a80d-ddcd639b1682",
      summary_100: "3de11722-225a-443e-9f78-a326cc37a1e6",
      summary_300: "76e0493f-cbcd-4e9f-9cfc-13d6e138cf0a",
      summary_500: "409622ac-7cd5-4209-9635-6413e49c009f",
    };
  }

  return {
    anonymize: "22b0f329-eea4-4632-b807-dc8fddf79b98",
    summary: "30ce991e-9fa8-4635-b934-e82c66e13f0c",
    summary_100: "40598bbe-5499-4ebf-bc59-ec5077dcd8b3",
    summary_300: "523d0b6d-9763-4687-b4fa-f3f32d81545c",
    summary_500: "55969598-b13c-4278-84f7-2f1be03a7688",
  };
};

export const DEFAULT_CHUNK_SIZE = 800;
export const DEFAULT_OVERLAP = 80;
export const DEFAULT_CHUNK_NUMBER = 15;
export const DEFAULT_EMBEDDING_MODEL = "multilingual-e5-large";
